import { DetailsView } from "@components/common/DetailsView";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { fields } from "./Fields";
import { Caisse, CaisseIdentity } from "@gen/caisse/v1/caisse_pb";
import { Agency } from "@gen/agency/v1/agency_pb";
import { getAgencies } from "@services/agency";
import { getCaisse, getCaisseIds, updateCaisse } from "@services/caisse";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import { useUserStore } from "@stores/userStore";
import { Role } from "@gen/user/v1/user_pb";

export function CaisseDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [caisse, setCaisse] = useState<Caisse>();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [caisseIds, setCaisseIds] = useState<CaisseIdentity[]>([]);
  const { addToast } = useToaster();
  const { user } = useUserStore();

  const fetchCaisse = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      const caisse = await getCaisse(parseInt(id, 10));
      if (!caisse) {
        return;
      }
      setCaisse(caisse);
    } catch (error) {
      console.error("Error fetching caisse:", error);
    }
  }, [id]);

  async function submitCaisse(caisse: Caisse) {
    try {
      await updateCaisse(caisse);
      addToast("info", t("admin.caisse.caisseUpdated"));
      fetchCaisse();
    } catch (e) {
      if (e instanceof ConnectError) {
        addToast("error", e.rawMessage);
      }
    }
  }

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    fetchCaisse();
  }, [fetchCaisse]);

  useEffect(() => {
    (async () => {
      try {
        setAgencies((await getAgencies()).agencies);
        setCaisseIds((await getCaisseIds()).identities);
      } catch (error) {
        console.error("Error fetching agencies or caisse IDs:", error);
      }
    })();
  }, []);

  return (
    <DetailsView
      item={caisse}
      title={`Caisse ${caisse?.name}`}
      fields={fields(t, agencies, caisseIds)}
      isEditable={!!user && [Role.ADMIN, Role.TRAVEL_AGENT].includes(user.role)}
      isLoading={!agencies || agencies.length == 0 || !caisse}
      onSubmit={submitCaisse}
    />
  );
}
